import type { ReactNode } from "react";
import { CircleHelp, List, Sparkles } from "lucide-react";

import { cn } from "@/modern/utils";

import type { NotificationSummaryModel } from "../types";
import { updatesIdleEmptyCopy, type UpdatesInboxMode, type UpdatesHistoryRange } from "../utils/timeWindow";
import { SeeEarlierUpdatesButton } from "./NotificationCaughtUpEmpty";
import { NotificationTileButton } from "./NotificationTileButton";
import { NotificationWindowChrome } from "./NotificationTimeWindowControl";

const SUMMARY_CAPTION_LARGE_CLS = "text-lg leading-snug sm:text-xl";
const SUMMARY_CAPTION_COMPACT_CLS = "text-base leading-snug";
const SUMMARY_EYEBROW_LARGE_CLS =
  "text-sm font-bold leading-none tracking-[0.16em] text-foreground sm:text-base";
const SUMMARY_EYEBROW_COMPACT_CLS =
  "text-xs font-bold leading-none tracking-[0.16em] text-foreground sm:text-sm";
/** Shared under-label blurb — quieter than the title and count caption. */
const SUMMARY_DESCRIPTION_CLS =
  "text-sm font-normal leading-snug text-foreground/75 sm:text-[0.9375rem]";
/** Notable column spans two rows and fills available height. */
const NOTABLE_COLUMN_CLS = "h-full min-h-0 sm:row-span-2";

const NOTABLE_DESCRIPTION =
  "The updates that deserve your attention first — the ones most likely to matter when you open this inbox.";
const ROUTINE_DESCRIPTION =
  "Everyday board activity — imports, schedules, reminders, and similar updates.";
const ASK_DESCRIPTION =
  "Ask about what showed up in this inbox.";

export function NotificationSummaryView({
  summary,
  onOpenNotable,
  onOpenRoutine,
  onOpenAsk,
  closeButton,
  inboxMode,
  onInboxModeChange,
  historyRange,
  onHistoryRangeChange,
  onSeeEarlier,
}: {
  summary: NotificationSummaryModel;
  onOpenNotable: () => void;
  onOpenRoutine: () => void;
  onOpenAsk: () => void;
  closeButton?: ReactNode;
  inboxMode: UpdatesInboxMode;
  onInboxModeChange: (next: UpdatesInboxMode) => void;
  historyRange: UpdatesHistoryRange;
  onHistoryRangeChange: (next: UpdatesHistoryRange) => void;
  onSeeEarlier: () => void;
}) {
  const notableCount = summary.notable;
  const routineCount = Math.max(0, summary.total - summary.notable);
  const hasNotables = notableCount > 0;
  const hasRoutine = routineCount > 0;
  const bothEmpty = !hasNotables && !hasRoutine;
  const caughtUpCopy = updatesIdleEmptyCopy(inboxMode, {
    title: "You're all caught up",
    caption: "Check back when something new arrives.",
  });

  return (
    <div className="flex h-full min-h-0 flex-col px-5 pb-5 pt-5 sm:px-6">
      <header className="mb-4 flex shrink-0 items-center justify-between gap-4">
        <h1 className="min-w-0 text-[2.5rem] font-normal leading-[1.1] tracking-tight text-foreground">
          Updates
        </h1>
        <div className="flex shrink-0 items-center gap-2">
          <NotificationWindowChrome
            inboxMode={inboxMode}
            onInboxModeChange={onInboxModeChange}
            historyRange={historyRange}
            onHistoryRangeChange={onHistoryRangeChange}
          />
          {closeButton}
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-2 sm:grid-rows-[minmax(0,1fr)_minmax(0,1fr)]">
        {bothEmpty ? (
          <EmptyMessage
            className={NOTABLE_COLUMN_CLS}
            title={caughtUpCopy.title}
            caption={caughtUpCopy.caption}
            eyebrowClassName={SUMMARY_EYEBROW_LARGE_CLS}
            captionClassName={SUMMARY_CAPTION_LARGE_CLS}
            action={
              inboxMode === "unread" ? (
                <SeeEarlierUpdatesButton className="mt-4" onClick={onSeeEarlier} />
              ) : null
            }
          />
        ) : hasNotables ? (
          <NotificationTileButton
            onClick={onOpenNotable}
            className={cn(NOTABLE_COLUMN_CLS, "min-h-0")}
            big
            eyebrowClassName={SUMMARY_EYEBROW_LARGE_CLS}
            descriptionClassName={SUMMARY_DESCRIPTION_CLS}
            captionClassName={SUMMARY_CAPTION_LARGE_CLS}
            classes={{ bg: "bg-violet-500", text: "text-white", ring: "ring-violet-500/20" }}
            eyebrow="Notable"
            description={NOTABLE_DESCRIPTION}
            value={String(notableCount)}
            caption="Failures, decisions, and unusual changes"
            icon={CircleHelp}
          />
        ) : (
          <EmptyMessage
            className={NOTABLE_COLUMN_CLS}
            title="All clear"
            caption="Nothing needs your attention."
            eyebrowClassName={SUMMARY_EYEBROW_LARGE_CLS}
            captionClassName={SUMMARY_CAPTION_LARGE_CLS}
          />
        )}

        {!bothEmpty ? (
          hasRoutine ? (
            <NotificationTileButton
              onClick={onOpenRoutine}
              className="h-full min-h-0"
              eyebrowClassName={SUMMARY_EYEBROW_COMPACT_CLS}
              descriptionClassName={SUMMARY_DESCRIPTION_CLS}
              captionClassName={SUMMARY_CAPTION_COMPACT_CLS}
              classes={{ bg: "bg-amber-500", text: "text-white", ring: "ring-amber-500/20" }}
              eyebrow="Routine"
              description={ROUTINE_DESCRIPTION}
              value={String(routineCount)}
              caption="By category"
              icon={List}
            />
          ) : (
            <EmptyMessage
              className="h-full min-h-0"
              title="Quiet for now"
              caption="No routine activity."
              eyebrowClassName={SUMMARY_EYEBROW_COMPACT_CLS}
              captionClassName={SUMMARY_CAPTION_COMPACT_CLS}
            />
          )
        ) : null}

        <NotificationTileButton
          onClick={onOpenAsk}
          className={cn("h-full min-h-0", bothEmpty && NOTABLE_COLUMN_CLS)}
          eyebrowClassName={
            bothEmpty ? SUMMARY_EYEBROW_LARGE_CLS : SUMMARY_EYEBROW_COMPACT_CLS
          }
          descriptionClassName={SUMMARY_DESCRIPTION_CLS}
          captionClassName={
            bothEmpty ? SUMMARY_CAPTION_LARGE_CLS : SUMMARY_CAPTION_COMPACT_CLS
          }
          classes={{ bg: "bg-teal-500", text: "text-white", ring: "ring-teal-500/20" }}
          eyebrow="Ask Swivu"
          description={ASK_DESCRIPTION}
          value="What happened?"
          caption={bothEmpty ? "Failures, schedule changes, and more" : "About this inbox"}
          icon={Sparkles}
        />
      </div>
    </div>
  );
}

function EmptyMessage({
  title,
  caption,
  className,
  captionClassName,
  eyebrowClassName,
  action,
}: {
  title: string;
  caption: string;
  className?: string;
  captionClassName?: string;
  eyebrowClassName?: string;
  action?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex min-h-0 flex-col justify-between overflow-hidden rounded-[1.5rem] border border-border/60 bg-muted/20 p-5",
        "text-left",
        className,
      )}
    >
      <span
        className={cn(
          "shrink-0 font-bold uppercase text-foreground/70",
          eyebrowClassName ?? SUMMARY_EYEBROW_COMPACT_CLS,
        )}
      >
        Updates
      </span>
      <div className="relative z-[1] mt-auto min-w-0 shrink-0">
        <p className="text-4xl font-bold leading-[0.95] tracking-tight text-foreground">
          {title}
        </p>
        <p className={cn("mt-2 font-medium text-foreground/75", captionClassName ?? SUMMARY_CAPTION_COMPACT_CLS)}>
          {caption}
        </p>
        {action}
      </div>
    </div>
  );
}
